import { createRouter, createWebHistory, createWebHashHistory } from 'vue-router'
import { getToken, setToken, setMallToken, getUserInfo, setPendingShareId, getPendingShareId, removePendingShareId } from '@/utils/auth'
import routes from './routes'
import ShareManager from '@/utils/share'

// ===== 绑定去重 & 冷却（模块级，跨路由导航有效）=====
const _bindingNow = new Set()                           // 正在绑定中的 shareId|userId 组合
const _bindCooldown = new Map()                         // shareId|userId → 下次可重试的时间戳
const BIND_COOLDOWN_MS = 30 * 1000                     // 同一组合 30 秒内不重复尝试

function _comboKey (shareId, userId) {
  return `${shareId}__${userId}`
}

/** 检查 + 设置冷却。返回 true 表示现在可以尝试绑定（不在冷却中） */
function _checkCooldown (key) {
  const until = _bindCooldown.get(key) || 0
  if (Date.now() < until) return false
  _bindCooldown.set(key, Date.now() + BIND_COOLDOWN_MS)
  return true
}

// 路由配置已从routes.js导入

// 处理 URL 中的 token 参数
function handleUrlToken (to, next) {
  const accessToken = to.query.accessToken
  const refreshToken = to.query.refreshToken

  if (accessToken) {
    console.log('检测到 URL 中的 token，正在保存...')
    // 保存 accessToken
    setToken(accessToken, true)
    
    // 如果有 refreshToken，也保存（作为 mallToken 或其他用途）
    if (refreshToken) {
      setMallToken(refreshToken)
    }

    // 清除 URL 中的 token 参数
    const newQuery = { ...to.query }
    delete newQuery.accessToken
    delete newQuery.refreshToken
    
    // 构建新的路由
    next({
      path: to.path,
      query: newQuery,
      replace: true
    })
    return true
  }
  return false
}

// 创建路由函数，支持动态 base 和 app_id
export function createAppRouter (base = '/', appId) {
  const finalRoutes = [...routes]

  if (appId) {
    console.log('创建路由，app_id:', appId, 'base:', base)
  }

  const router = createRouter({
    history: createWebHistory(base),
    routes: finalRoutes,
    scrollBehavior (to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition
      } else {
        return { top: 0 }
      }
    }
  })

  router.beforeEach((to, from, next) => {
    console.log('路由导航:', from.path, '→', to.path)
    document.title = to.meta.title || '数字名片'

    // 优先处理 URL 中的 token 参数
    if (handleUrlToken(to, next)) {
      return
    }

    // ====== 🎯 全局捕获分享链接里的 share_id ======
    // 任何页面带 ?share_id=xxx 都要：
    //   a) 持久化到 localStorage（跨页面跳转/刷新不丢）
    //   b) 已登录 → 先清 localStorage 再异步绑定（绑定失败也不阻塞，也不重复）
    //   c) 未登录 → 保留 localStorage，注册/登录成功后自动绑定
    if (to.query.share_id) {
      const incomingShareId = String(to.query.share_id)
      console.log('🔗 捕获到分享链接 share_id =', incomingShareId)

      // 持久化
      setPendingShareId(incomingShareId)

      // 已登录 → 自动绑定（fire-and-forget，不阻塞路由）
      const token = getToken()
      const userInfo = getUserInfo()
      if (token && userInfo && userInfo.id) {
        const userId = userInfo.id
        const key = _comboKey(incomingShareId, userId)

        // 双重去重：内存标记 + 冷却
        if (!_bindingNow.has(key) && _checkCooldown(key)) {
          _bindingNow.add(key)
          // ⚡ 先清 localStorage —— 避免后续 replace 触发第二遍导航时又走到 pendingShareId 分支重复调
          removePendingShareId()

          console.log('🔗 用户已登录，自动绑定推荐关系:', { userId, incomingShareId })
          ShareManager.bindForCurrentUser(incomingShareId, userId).then(result => {
            if (result?.ok || result?.skipped) {
              console.log('🔗 推荐关系绑定成功/跳过:', result)
            } else {
              // 失败了放回 localStorage，下次登录/刷新还能再试
              setPendingShareId(incomingShareId)
            }
          }).catch(err => {
            console.warn('🔗 推荐关系绑定异常（放回 localStorage）:', err?.message)
            setPendingShareId(incomingShareId)
          }).finally(() => {
            _bindingNow.delete(key)
          })
        }

        // 从 URL 清除 share_id，防止刷新重复触发
        const cleanQuery = { ...to.query }
        delete cleanQuery.share_id
        next({ path: to.path, query: cleanQuery, replace: true })
        return
      }

      // 未登录 → 放行（URL 保留 share_id，注册/登录页自己会读）
      next()
      return
    }

    // ====== 已登录但 localStorage 里还有未绑定的 share_id（刷新/另一个标签登录等场景） ======
    const pendingShareId = getPendingShareId()
    if (pendingShareId && getToken()) {
      const userInfo = getUserInfo()
      if (userInfo && userInfo.id) {
        const key = _comboKey(pendingShareId, userInfo.id)
        if (!_bindingNow.has(key) && _checkCooldown(key)) {
          _bindingNow.add(key)
          console.log('🔗 发现待绑定的 share_id，用户已登录，立即绑定:', pendingShareId)
          ShareManager.bindForCurrentUser(pendingShareId, userInfo.id).then(result => {
            if (result?.ok || result?.skipped) {
              removePendingShareId()
            }
          }).catch(() => {}).finally(() => {
            _bindingNow.delete(key)
          })
        }
      }
    }

    if (to.meta.requiresAuth) {
      if (getToken()) {
        next()
      } else {
        next({ name: 'Login', query: { redirect: to.fullPath } })
      }
    } else {
      next()
    }
  })

  return router
}

// 默认导出，用于独立运行
const router = createAppRouter(window.__POWERED_BY_QIANKUN__ ? '/subapp' : '/', window.__SUB_APP_ID__)

export default router
